import { useTrackLink } from '@seek/cmsu-analytics';
import type { Article, TopicListFragment, Topic } from '@seek/cmsu-cms-connect';
import { richTextHelper } from '@seek/cmsu-rich-text/src/utils/richTextHelper';
import type { Brand, Language } from '@seek/melways-sites';
import {
  Box,
  Dropdown,
  Heading,
  Stack,
  Text,
  Tiles,
} from 'braid-design-system';
import type React from 'react';
import { useEffect, useState } from 'react';

import { ITEM_DISPLAY_TYPE } from '../../helpers/articleHelper';
import { ArticleItem } from '../ArticleItem/ArticleItem';
import {
  articleItemHelper,
  type ArticleItemFieldVisibility,
} from '../ArticleItem/articleItemHelper';

type Props = {
  topicList: TopicListFragment;
  brand: Brand;
  language: Language;
};

export const TopicList = ({ topicList, brand, language }: Props) => {
  const defaultTopic = getDefaultTopic(topicList);
  const [selectedTopic, setSelectedTopic] = useState<string>(defaultTopic);
  const [currentTopic, setCurrentTopic] = useState<Topic | undefined>(
    undefined,
  );
  const trackDropdownLink = useTrackLink('dropdown-list');
  const trackArticleItemLink = useTrackLink('text-link');
  useEffect(() => {
    if (topicList)
      setCurrentTopic(findTopicBy(topicList.topics as Topic[], selectedTopic));
  }, [selectedTopic, topicList, topicList.topics]);

  if (!isTopicListValid(topicList)) return;

  const articleItemFieldVisibility: ArticleItemFieldVisibility =
    articleItemHelper.mapToArticleFieldVisibility(topicList.cardFields);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const trackingAttributes = {
      elementType: 'dropdown',
      elementAction: 'apply filter',
      elementName: 'article subject',
      elementSelectedOption: event.currentTarget.value,
    };
    trackDropdownLink?.(trackingAttributes);
    setSelectedTopic(event.currentTarget.value);
  };

  return (
    <Stack space="large">
      <Tiles
        space="gutter"
        columns={{
          mobile: 1,
          tablet: 2,
          desktop: 2,
          wide: 2,
        }}
      >
        <Box display="flex" alignItems="center" height="full">
          <Heading level="2">{topicList.topicFilterLabel}</Heading>
        </Box>
        <Dropdown
          id="ARTICLE_GROUP"
          label=""
          aria-label="Select a topic"
          onChange={handleChange}
          value={selectedTopic}
          placeholder={topicList.topicFilterPlaceholder || ''}
        >
          <>
            {topicList.topics.map((topic, index) => (
              <option key={index} value={topic.name}>
                {topic.name}
              </option>
            ))}
          </>
        </Dropdown>
      </Tiles>
      {currentTopic && currentTopic.description && (
        <Text>
          {richTextHelper.getTextWithoutFormatting(currentTopic.description)}
        </Text>
      )}
      <Tiles
        space="gutter"
        columns={{
          mobile: 1,
          tablet: 2,
          desktop: 3,
          wide: 3,
        }}
      >
        {currentTopic &&
          currentTopic.relatedArticlesInTopic &&
          currentTopic.relatedArticlesInTopic.map((article, index) => (
            <ArticleItem
              displayType={ITEM_DISPLAY_TYPE.CARD}
              article={article as Article}
              brand={brand}
              language={language}
              fieldVisibility={articleItemFieldVisibility}
              key={index}
              trackLink={trackArticleItemLink}
            />
          ))}
      </Tiles>
    </Stack>
  );
};

const isTopicListValid = (topicList: TopicListFragment): boolean =>
  (topicList && topicList.topics && topicList.topics.length > 0) || false;

const getDefaultTopic = (topicList: TopicListFragment): string =>
  isTopicListValid(topicList) && topicList.setDefaultTopic
    ? topicList.topics[0].name
    : '';

const findTopicBy = (topics: Topic[], topicName: string): Topic | undefined =>
  topics.find((topic) => topic.name === topicName);

export const topicListHelper = {
  isTopicListValid,
  getDefaultTopic,
  findTopicBy,
};
